import { normalizePageTabId } from '@/common/composables/useFormStates'
import { extractScriptNodeText } from '@/common/utils/scriptNodeText'
import type { ScriptData } from '@/components/platform/types'

export function readXmlNodeText(node: unknown): string {
  return extractScriptNodeText(node).trim()
}

export function resolvePageTabJsName(tab: unknown): string {
  if (!tab || typeof tab !== 'object') return ''
  const rec = tab as Record<string, unknown>
  const fromField = readXmlNodeText(rec.jsName)
  if (fromField) return fromField
  const attrs =
    rec._attributes && typeof rec._attributes === 'object'
      ? (rec._attributes as Record<string, unknown>)
      : {}
  return readXmlNodeText(attrs.jsName)
}

function resolvePageTabTitle(tab: unknown): string {
  if (!tab || typeof tab !== 'object') return ''
  const rec = tab as Record<string, unknown>
  return readXmlNodeText(rec.name)
}

function asPageTabId(
  value: unknown,
): string | number | { _text?: string } | null | undefined {
  if (value == null || typeof value === 'string' || typeof value === 'number') {
    return value
  }
  if (typeof value === 'object' && '_text' in value) {
    return value as { _text?: string }
  }
  return String(value)
}

export function collectPageTabVisibilityKeys(tab: unknown): string[] {
  if (!tab || typeof tab !== 'object') return []
  const rec = tab as Record<string, unknown>
  const attrs =
    rec._attributes && typeof rec._attributes === 'object'
      ? (rec._attributes as Record<string, unknown>)
      : {}
  const keys = [
    normalizePageTabId(asPageTabId(rec.id ?? attrs.id)),
    resolvePageTabJsName(tab),
    resolvePageTabTitle(tab),
  ]
  return [...new Set(keys.filter((key) => key.length > 0))]
}

export type LayoutPageTabMatch = {
  tab: unknown
  id: string
  jsName: string
}

function isPanelLikeNode(node: Record<string, unknown>): boolean {
  return Array.isArray(node.rows) && !Array.isArray(node.panelRows)
}

function flattenPageTabNodes(node: unknown, out: unknown[]): void {
  if (node == null) return
  if (Array.isArray(node)) {
    for (const item of node) flattenPageTabNodes(item, out)
    return
  }
  if (typeof node !== 'object') return
  const rec = node as Record<string, unknown>
  if (Array.isArray(rec.main) || Array.isArray(rec.header) || Array.isArray(rec.footer)) {
    flattenPageTabNodes(rec.main, out)
    flattenPageTabNodes(rec.header, out)
    flattenPageTabNodes(rec.footer, out)
    return
  }
  if (isPanelLikeNode(rec)) return
  if (Array.isArray(rec.panelRows) || collectPageTabVisibilityKeys(node).length > 0) {
    out.push(node)
  }
}

function unwrapFormLayout(formOrPage: unknown): unknown {
  if (!formOrPage || typeof formOrPage !== 'object') return formOrPage
  const rec = formOrPage as Record<string, unknown>
  const form = rec.form
  if (form && typeof form === 'object' && !Array.isArray(form)) {
    const inner = (form as Record<string, unknown>).form
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return { ...(form as Record<string, unknown>), ...(inner as Record<string, unknown>) }
    }
    return form
  }
  return formOrPage
}

/** Tabs from parsed form.main / header / footer (nested arrays after getTabs). */
export function collectLayoutPageTabs(formOrPage: unknown): unknown[] {
  const out: unknown[] = []
  flattenPageTabNodes(unwrapFormLayout(formOrPage), out)
  return out
}

/**
 * Swing `setVisible(String name)` matches page `getName()` (XML `<name>` / jsName),
 * xml id, or tab title. Used to persist PAGE states before Section mounts.
 */
export function findLayoutPageTabByVisibilityName(
  formOrPage: unknown,
  name: string | number | null | undefined,
): LayoutPageTabMatch | null {
  const needle = String(name ?? '').trim()
  if (!needle) return null
  const needleId = normalizePageTabId(asPageTabId(needle))
  for (const tab of collectLayoutPageTabs(formOrPage)) {
    const keys = collectPageTabVisibilityKeys(tab)
    if (!keys.includes(needle) && !(needleId && keys.includes(needleId))) continue
    const rec = tab as Record<string, unknown>
    const attrs =
      rec._attributes && typeof rec._attributes === 'object'
        ? (rec._attributes as Record<string, unknown>)
        : {}
    return {
      tab,
      id: normalizePageTabId(asPageTabId(rec.id ?? attrs.id)),
      jsName: resolvePageTabJsName(tab),
    }
  }
  return null
}

export function isPageTabVisible(
  tab: unknown,
  visibilityByKey: Record<string, boolean>,
): boolean {
  if (!visibilityByKey || Object.keys(visibilityByKey).length === 0) return true
  for (const key of collectPageTabVisibilityKeys(tab)) {
    if (Object.prototype.hasOwnProperty.call(visibilityByKey, key)) {
      return visibilityByKey[key] !== false
    }
  }
  return true
}

export function buildPageOnClickScriptData(tab: {
  onClick?: string
  web_client_exec?: boolean
}): ScriptData | undefined {
  const script = typeof tab?.onClick === 'string' ? tab.onClick.trim() : ''
  if (!script) return undefined
  const node: ScriptData = { _text: script }
  if (tab.web_client_exec !== undefined) {
    node._attributes = { web_client_exec: tab.web_client_exec }
  }
  return node
}
